import { useApolloClient, useMutation } from '@apollo/client';
import { useAppDispatch } from 'src/app/hooks';
import { reducerUpdateWidgetMetaDataList } from 'src/features/customDashboard';
import {
  CUSTOM_DASHBOARD_DOWNLOAD_PDF,
  GET_CUSTOM_DASHBOARD_WIDGET_DATA,
  GET_CUSTOM_DASHBOARD_WIDGET_METADATA
} from './graphql/customDashboard/query';
import { reducerUpdateDashboardList } from 'src/features/customDashboard';
import { GET_CUSTOM_DASHBOARD } from './graphql/customDashboard/query';
import {
  ADD_CUSTOM_DASHBOARD_V2,
  CUSTOM_DASHBOARD_CREATE_JOB_DOWNLOAD,
  CUSTOM_DASHBOARD_CREATE_JOB_DOWNLOAD_OFM,
  CUSTOM_DASHBOARD_CREATE_JOB_DOWNLOAD_ONM,
  CUSTOM_DASHBOARD_EDIT_WHITE_LABEL,
  CUSTOM_DASHBOARD_MANAGE_WIDGET,
  DELETE_CUSTOM_DASHBOARD,
  EDIT_CUSTOM_DASHBOARD
} from './graphql/customDashboard/mutation';
import {
  IDashboardObject,
  IDashboardWidgetDataPayload,
  IDataWidgetCustomDasboard,
  IWidgetMetaData,
  TCustomDashboardSourceType
} from 'src/models/customDashboard';
import { IGranularity } from 'src/models/general';

interface IGetCustomDashboardResponse {
  customDashboard_getCustomDashboard: Array<IDashboardObject>;
}

interface IGetWidgetMetadataResponse {
  customDashboard_getCustomDashboardWidgetMetadata: Array<IWidgetMetaData>;
}

interface IAddCustomDashboardResponse {
  customDashboard_addCustomDashboard: IDashboardObject;
}

export interface IAddCustomDashboardPayload {
  projectId: string;
  displayName: string;
  dashboardSourceType: TCustomDashboardSourceType;
  objectIds?: string[];
  clippingId?: string;
  objectGroupIdsOption?: string[];
}

export interface IDownloadJobOnmOfmPayload {
  projectId: string;
  start: string | Date;
  end: string | Date;
  mediaList: string[];
  granularity: IGranularity;
  timezone: number;
  customDashboardId: string;
  isWhiteLabel: boolean;
  clippingId: string;
}

export interface IDownloadJobOnmOfmResponse {
  id: string;
  projectId: string;
  customDashboardId: string;
  objectIds: string[];
  startDate: string;
  endDate: string;
  timezoneOffset: number;
  clippingId: string;
}

export interface IDownloadJobOnmResponse {
  customDashboard_createJobDownloadCustomDashboardOnm: IDownloadJobOnmOfmResponse;
}

export interface IDownloadJobOfmResponse {
  customDashboard_createJobDownloadCustomDashboardOfm: IDownloadJobOnmOfmResponse;
}

const useCustomDashboard = function () {
  const client = useApolloClient();
  const dispatch = useAppDispatch();

  const fetchCustomDashboardList = async function (projectId: string) {
    return await client
      .query<IGetCustomDashboardResponse>({
        query: GET_CUSTOM_DASHBOARD,
        variables: {
          projectId
        }
      })
      .then((response) => {
        return {
          ...response,
          data: response.data.customDashboard_getCustomDashboard
        };
      });
  };

  const getCustomDashboardList = async function (projectId: string) {
    const response = await fetchCustomDashboardList(projectId);
    dispatch(reducerUpdateDashboardList(response));
  };
  // const [objectManagementAddDashboard, resObjectManagementAddDashboard] =
  //   useMutation(ADD_CUSTOM_DASHBOARD);

  const [objectManagementEditDashboard, resObjectManagementEditDashboard] =
    useMutation(EDIT_CUSTOM_DASHBOARD);

  const fetchCustomDashboardWidgetMetadata = async function (
    sourceType: TCustomDashboardSourceType
  ) {
    const response = await client.query<IGetWidgetMetadataResponse>({
      query: GET_CUSTOM_DASHBOARD_WIDGET_METADATA,
      fetchPolicy: 'network-only',
      variables: { dashboardSourceType: sourceType }
    });
    return {
      ...response,
      data: response.data.customDashboard_getCustomDashboardWidgetMetadata
    };
  };

  const getCustomDashboardWidgetMetadata = async function (
    sourceType: TCustomDashboardSourceType
  ) {
    const response = await fetchCustomDashboardWidgetMetadata(sourceType);
    dispatch(reducerUpdateWidgetMetaDataList(response));
  };

  const fetchCustomDashboardWidgetData = async function (
    payload: IDashboardWidgetDataPayload
  ) {
    const response = await client.query<IDataWidgetCustomDasboard>({
      query: GET_CUSTOM_DASHBOARD_WIDGET_DATA,
      variables: payload,
      fetchPolicy: 'network-only'
    });
    return {
      ...response,
      data: response.data
    };
  };

  const getCustomDashboardWidgetData = async function (
    payload: IDashboardWidgetDataPayload
  ) {
    const response = await fetchCustomDashboardWidgetData(payload);
    return response;
  };

  const fetchCustomDashboardDownloadPdf = async function (
    payload: IDashboardWidgetDataPayload
  ) {
    const response = await client.query<{ url: string }>({
      query: CUSTOM_DASHBOARD_DOWNLOAD_PDF,
      variables: payload,
      fetchPolicy: 'network-only'
    });
    return {
      ...response,
      data: response.data
    };
  };

  const getCustomDashboardDownloadPdf = async function (
    payload: IDashboardWidgetDataPayload
  ) {
    try {
      const response = await fetchCustomDashboardDownloadPdf(payload);
      window.open(response.data.url, '_blank');
    } catch (err) {
      console.log(err);
    }
  };

  const [manageWidgetCustomDashboard, resManageWidgetCustomDashboard] =
    useMutation(CUSTOM_DASHBOARD_MANAGE_WIDGET);

  const [editCustomDahsboard, resEditCustomDashboard] = useMutation(
    CUSTOM_DASHBOARD_EDIT_WHITE_LABEL
  );

  const [deleteCustomDashboard, resDeleteCustomDashboard] = useMutation(
    DELETE_CUSTOM_DASHBOARD
  );

  const [createDownloadJob, resCreateDownloadJob] = useMutation(
    CUSTOM_DASHBOARD_CREATE_JOB_DOWNLOAD
  );

  const [addCustomDashboard, resAddCustomDashboard] = useMutation<
    IAddCustomDashboardResponse,
    IAddCustomDashboardPayload
  >(ADD_CUSTOM_DASHBOARD_V2);

  const [createDownloadJobOnm, resCreateDownloadJobOnm] = useMutation<
    IDownloadJobOnmResponse,
    IDownloadJobOnmOfmPayload
  >(CUSTOM_DASHBOARD_CREATE_JOB_DOWNLOAD_ONM);

  const [createDownloadJobOfm, resCreateDownloadJobOfm] = useMutation<
    IDownloadJobOfmResponse,
    IDownloadJobOnmOfmPayload
  >(CUSTOM_DASHBOARD_CREATE_JOB_DOWNLOAD_OFM);

  return {
    getCustomDashboardWidgetMetadata,
    getCustomDashboardList,
    // objectManagementAddDashboard,
    // resObjectManagementAddDashboard,
    objectManagementEditDashboard,
    resObjectManagementEditDashboard,
    manageWidgetCustomDashboard,
    resManageWidgetCustomDashboard,
    getCustomDashboardWidgetData,
    getCustomDashboardDownloadPdf,
    editCustomDahsboard,
    resEditCustomDashboard,
    deleteCustomDashboard,
    resDeleteCustomDashboard,
    createDownloadJob,
    resCreateDownloadJob,
    addCustomDashboard,
    resAddCustomDashboard,
    createDownloadJobOnm,
    resCreateDownloadJobOnm,
    createDownloadJobOfm,
    resCreateDownloadJobOfm
  };
};

export default useCustomDashboard;
